import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { Preferences } from '@capacitor/preferences';

export type GlassTint = 'cool' | 'warm' | 'ocean' | 'midnight';

interface GlassTintContextValue {
  tint: GlassTint;
  setTint: (t: GlassTint) => void;
  toggle: () => void;
}

const GlassTintContext = createContext<GlassTintContextValue>({
  tint: 'cool',
  setTint: () => {},
  toggle: () => {},
});

const STORAGE_KEY = 'wellebit-glass-tint';

const validTints: GlassTint[] = ['cool', 'warm', 'ocean', 'midnight'];

function isGlassTint(value: string | null | undefined): value is GlassTint {
  return !!value && validTints.includes(value as GlassTint);
}

async function getStoredTint(): Promise<GlassTint> {
  try {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (isGlassTint(value)) return value;
  } catch {}

  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (isGlassTint(value)) return value;
  } catch {}

  return 'cool';
}

async function persistTint(tint: GlassTint) {
  try {
    await Preferences.set({ key: STORAGE_KEY, value: tint });
  } catch {}

  try {
    localStorage.setItem(STORAGE_KEY, tint);
  } catch {}
}

const tintLabel: Record<GlassTint, string> = {
  cool: 'Cool Teal',
  warm: 'Warm Rose',
  ocean: 'Ocean Blue',
  midnight: 'Midnight Purple',
};

/**
 * CSS variable overrides per tint. Applied to <html>.
 *
 * Variables like --glass-bg, --glass-border etc. store **base HSL** values
 * (e.g. "170 25% 45%") WITHOUT opacity — opacity is applied at usage site
 * via `hsl(var(--glass-bg) / 0.10)`.
 *
 * Variables like --glass-shadow store full composite values.
 */
/** Shared foreground tokens — white-on-glass hierarchy for all tints */
const sharedForegrounds: Record<string, string> = {
  '--foreground': '0 0% 100%',
  '--card-foreground': '0 0% 100%',
  '--popover-foreground': '0 0% 100%',
  '--muted-foreground': '0 0% 80%',
  '--secondary-foreground': '0 0% 95%',
  '--primary-foreground': '0 0% 4%',
  '--accent-foreground': '0 0% 100%',
  '--highlight-foreground': '0 0% 100%',
};

const tintOverrides: Record<GlassTint, Record<string, string>> = {
  cool: {
    ...sharedForegrounds,
    '--glass-bg': '170 12% 32%',
    '--glass-bg-elevated': '170 14% 36%',
    '--glass-bg-active': '170 16% 38%',
    '--glass-border': '170 14% 55%',
    '--glass-border-elevated': '170 16% 58%',
    '--glass-border-active': '170 18% 62%',
    '--glass-glow': '170 35% 42%',
    '--glass-icon-bg': '170 18% 45%',
    '--glass-refraction': '170 18% 60%',
    '--glass-shadow': '0 8px 32px hsl(0 0% 0% / 0.45), 0 2px 8px hsl(0 0% 0% / 0.25), inset 0 0.5px 0 hsl(0 0% 100% / 0.10), inset 0 -1px 0 hsl(0 0% 0% / 0.15)',
    '--glass-shadow-elevated': '0 12px 40px hsl(0 0% 0% / 0.50), 0 4px 12px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.14), inset 0 -1px 0 hsl(0 0% 0% / 0.18)',
    '--glass-shadow-active': '0 8px 32px hsl(170 50% 40% / 0.18), 0 2px 8px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.16), inset 0 -1px 0 hsl(0 0% 0% / 0.20)',
    '--primary': '170 50% 55%',
    '--accent': '170 50% 55%',
    '--ring': '170 50% 55%',
    '--highlight': '170 50% 55%',
    '--highlight-muted': '170 40% 35%',
    '--card': '170 24% 38%',
    '--secondary': '170 18% 12%',
    '--muted': '170 16% 14%',
    '--popover': '170 20% 8%',
    '--whoop-teal': '170 50% 55%',
    '--whoop-teal-glow': '170 50% 65%',
    '--whoop-green': '170 45% 48%',
    '--whoop-gold': '170 30% 52%',
    '--whoop-purple': '170 40% 50%',
    '--gradient-whoop': 'linear-gradient(135deg, hsl(170 50% 55%), hsl(170 45% 40%))',
    '--glow-whoop': '0 0 30px hsl(170 50% 55% / 0.15)',
  },
  warm: {
    ...sharedForegrounds,
    '--glass-bg': '18 12% 34%',
    '--glass-bg-elevated': '18 14% 38%',
    '--glass-bg-active': '18 16% 40%',
    '--glass-border': '18 15% 56%',
    '--glass-border-elevated': '18 17% 60%',
    '--glass-border-active': '18 19% 64%',
    '--glass-glow': '15 30% 45%',
    '--glass-icon-bg': '15 18% 45%',
    '--glass-refraction': '18 14% 62%',
    '--glass-shadow': '0 8px 32px hsl(0 0% 0% / 0.45), 0 2px 8px hsl(0 0% 0% / 0.25), inset 0 0.5px 0 hsl(0 0% 100% / 0.10), inset 0 -1px 0 hsl(0 0% 0% / 0.15)',
    '--glass-shadow-elevated': '0 12px 40px hsl(0 0% 0% / 0.50), 0 4px 12px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.14), inset 0 -1px 0 hsl(0 0% 0% / 0.18)',
    '--glass-shadow-active': '0 8px 32px hsl(15 45% 40% / 0.18), 0 2px 8px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.16), inset 0 -1px 0 hsl(0 0% 0% / 0.20)',
    '--primary': '15 50% 60%',
    '--accent': '15 50% 60%',
    '--ring': '15 50% 60%',
    '--highlight': '15 45% 58%',
    '--highlight-muted': '15 35% 40%',
    '--card': '18 24% 40%',
    '--secondary': '18 18% 12%',
    '--muted': '18 16% 14%',
    '--popover': '18 20% 8%',
    '--whoop-teal': '15 50% 60%',
    '--whoop-teal-glow': '15 55% 70%',
    '--whoop-green': '15 45% 52%',
    '--whoop-gold': '25 55% 60%',
    '--whoop-purple': '345 50% 62%',
    '--gradient-whoop': 'linear-gradient(135deg, hsl(15 50% 60%), hsl(345 45% 52%))',
    '--glow-whoop': '0 0 30px hsl(15 50% 60% / 0.15)',
  },
  ocean: {
    ...sharedForegrounds,
    '--glass-bg': '220 13% 34%',
    '--glass-bg-elevated': '220 15% 38%',
    '--glass-bg-active': '220 17% 40%',
    '--glass-border': '220 17% 58%',
    '--glass-border-elevated': '220 19% 62%',
    '--glass-border-active': '220 21% 66%',
    '--glass-glow': '220 40% 48%',
    '--glass-icon-bg': '220 30% 45%',
    '--glass-refraction': '220 16% 64%',
    '--glass-shadow': '0 8px 32px hsl(0 0% 0% / 0.45), 0 2px 8px hsl(0 0% 0% / 0.25), inset 0 0.5px 0 hsl(0 0% 100% / 0.10), inset 0 -1px 0 hsl(0 0% 0% / 0.15)',
    '--glass-shadow-elevated': '0 12px 40px hsl(0 0% 0% / 0.50), 0 4px 12px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.14), inset 0 -1px 0 hsl(0 0% 0% / 0.18)',
    '--glass-shadow-active': '0 8px 32px hsl(220 60% 45% / 0.18), 0 2px 8px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.16), inset 0 -1px 0 hsl(0 0% 0% / 0.20)',
    '--primary': '220 60% 62%',
    '--accent': '220 60% 62%',
    '--ring': '220 60% 62%',
    '--highlight': '220 60% 60%',
    '--highlight-muted': '220 40% 40%',
    '--card': '220 26% 40%',
    '--secondary': '220 18% 12%',
    '--muted': '220 16% 14%',
    '--popover': '220 22% 8%',
    '--whoop-teal': '220 60% 62%',
    '--whoop-teal-glow': '220 70% 72%',
    '--whoop-green': '205 55% 55%',
    '--whoop-gold': '210 40% 60%',
    '--whoop-purple': '235 50% 65%',
    '--gradient-whoop': 'linear-gradient(135deg, hsl(220 60% 62%), hsl(205 58% 48%))',
    '--glow-whoop': '0 0 30px hsl(220 60% 62% / 0.15)',
  },
  midnight: {
    ...sharedForegrounds,
    '--glass-bg': '270 10% 32%',
    '--glass-bg-elevated': '270 12% 36%',
    '--glass-bg-active': '270 14% 38%',
    '--glass-border': '270 12% 56%',
    '--glass-border-elevated': '270 14% 60%',
    '--glass-border-active': '270 16% 64%',
    '--glass-glow': '270 28% 45%',
    '--glass-icon-bg': '270 22% 40%',
    '--glass-refraction': '270 14% 62%',
    '--glass-shadow': '0 8px 32px hsl(0 0% 0% / 0.45), 0 2px 8px hsl(0 0% 0% / 0.25), inset 0 0.5px 0 hsl(0 0% 100% / 0.10), inset 0 -1px 0 hsl(0 0% 0% / 0.15)',
    '--glass-shadow-elevated': '0 12px 40px hsl(0 0% 0% / 0.50), 0 4px 12px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.14), inset 0 -1px 0 hsl(0 0% 0% / 0.18)',
    '--glass-shadow-active': '0 8px 32px hsl(270 40% 40% / 0.18), 0 2px 8px hsl(0 0% 0% / 0.30), inset 0 0.5px 0 hsl(0 0% 100% / 0.16), inset 0 -1px 0 hsl(0 0% 0% / 0.20)',
    '--primary': '270 40% 65%',
    '--accent': '270 40% 65%',
    '--ring': '270 40% 65%',
    '--highlight': '270 40% 62%',
    '--highlight-muted': '270 30% 42%',
    '--card': '270 20% 38%',
    '--secondary': '270 14% 12%',
    '--muted': '270 12% 14%',
    '--popover': '270 16% 8%',
    '--whoop-teal': '270 40% 65%',
    '--whoop-teal-glow': '270 45% 74%',
    '--whoop-green': '255 35% 55%',
    '--whoop-gold': '280 25% 58%',
    '--whoop-purple': '285 45% 62%',
    '--gradient-whoop': 'linear-gradient(135deg, hsl(270 40% 65%), hsl(285 38% 50%))',
    '--glow-whoop': '0 0 30px hsl(270 40% 65% / 0.15)',
  },
};

function applyOverrides(overrides: Record<string, string>) {
  const targets = [document.documentElement, document.body].filter(Boolean) as HTMLElement[];
  for (const [key, value] of Object.entries(overrides)) {
    for (const target of targets) {
      target.style.setProperty(key, value);
    }
  }
}

export { tintLabel };

export function GlassTintProvider({ children }: { children: ReactNode }) {
  const [tint, setTintState] = useState<GlassTint>('cool');
  const [isHydrated, setIsHydrated] = useState(false);

  const setTint = (t: GlassTint) => {
    setTintState(t);
    void persistTint(t);
  };

  const toggle = () => {
    const order: GlassTint[] = ['cool', 'warm', 'ocean', 'midnight'];
    const idx = order.indexOf(tint);
    setTint(order[(idx + 1) % order.length]);
  };

  useEffect(() => {
    void (async () => {
      const storedTint = await getStoredTint();
      setTintState(storedTint);
      setIsHydrated(true);
    })();
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || !isGlassTint(event.newValue)) return;
      setTintState(event.newValue);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    applyOverrides(tintOverrides[tint] || tintOverrides.cool);
    if (isHydrated) {
      document.documentElement.dataset.glassTint = tint;
      document.body.dataset.glassTint = tint;
    }
  }, [tint, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;
    void persistTint(tint);
  }, [tint]);

  return (
    <GlassTintContext.Provider value={{ tint, setTint, toggle }}>
      {children}
    </GlassTintContext.Provider>
  );
}

export function useGlassTint() {
  return useContext(GlassTintContext);
}
